import React, { useEffect, useMemo, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Loader2, TrendingUp } from "lucide-react";
import api from "../../lib/api";

/**
 * RevenueChart — admin-only daily revenue bar chart for the last N days.
 * Uses recharts (already in package.json). Data source: GET
 * /api/employee/stats/revenue-daily?days=N returning {series: [{date, revenue, count}]}.
 * Dark-mode aware colors that match the rest of the dashboard.
 */
const RANGES = [7, 14, 30, 90];

export default function RevenueChart() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    setErr("");
    api.get(`/employee/stats/revenue-daily?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Chyba načítání"))
      .finally(() => setLoading(false));
  }, [days]);

  const summary = useMemo(() => {
    if (!data?.series) return null;
    const total = data.series.reduce((s, r) => s + (r.revenue || 0), 0);
    const count = data.series.reduce((s, r) => s + (r.count || 0), 0);
    const peakDay = data.series.reduce((best, r) => (r.revenue > (best?.revenue || 0) ? r : best), null);
    return { total, count, peakDay, avg: count ? total / count : 0 };
  }, [data]);

  // Friendly chart-axis labels: "10.6." for 10. červen
  const chartData = useMemo(() => (data?.series || []).map((r) => {
    const d = new Date(`${r.date}T00:00:00`);
    return { ...r, label: `${d.getDate()}.${d.getMonth() + 1}.` };
  }), [data]);

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="revenue-chart">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h4 className="font-display text-lg text-apex-cream font-semibold flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-apex-gold" />
            Tržby za posledních {days} dní
          </h4>
          {summary && (
            <p className="text-xs text-apex-cream/60 mt-1">
              Celkem <span className="text-apex-gold font-semibold">{Math.round(summary.total).toLocaleString("cs-CZ")} Kč</span> · {summary.count} rezervací
              {summary.peakDay?.revenue > 0 && (
                <> · rekord <span className="text-apex-cream">{new Date(summary.peakDay.date).toLocaleDateString("cs-CZ")}</span> ({Math.round(summary.peakDay.revenue).toLocaleString("cs-CZ")} Kč)</>
              )}
            </p>
          )}
        </div>
        <div className="inline-flex rounded-lg border border-apex-line bg-apex-bg/40 p-1" data-testid="revenue-range-switcher">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                days === r ? "bg-apex-gold text-apex-bg" : "text-apex-cream/65 hover:text-apex-cream"
              }`}
              data-testid={`revenue-range-${r}`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      <div className="h-[260px] w-full">
        {loading ? (
          <div className="h-full flex items-center justify-center text-apex-cream/55">
            <Loader2 className="w-5 h-5 animate-spin text-apex-gold" />
          </div>
        ) : err ? (
          <div className="h-full flex items-center justify-center text-red-300 text-sm">{err}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 5, right: 8, left: -8, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2f2925" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "rgba(245,240,232,0.55)", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#2f2925" }}
                interval={days > 30 ? Math.floor(days / 12) : days > 14 ? 2 : 0}
              />
              <YAxis
                tick={{ fill: "rgba(245,240,232,0.55)", fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: "#2f2925" }}
                tickFormatter={(v) => v >= 1000 ? `${Math.round(v / 1000)}k` : v}
              />
              <Tooltip
                cursor={{ fill: "rgba(229,200,128,0.07)" }}
                contentStyle={{
                  background: "#1f1b18",
                  border: "1px solid #2f2925",
                  borderRadius: 12,
                  color: "#f5f0e8",
                  fontSize: 12,
                  padding: "10px 12px",
                }}
                itemStyle={{ color: "#f5f0e8" }}
                labelStyle={{ color: "#f5f0e8", marginBottom: 4, fontWeight: 600 }}
                formatter={(value, name) => {
                  if (name === "revenue") return [`${Math.round(value).toLocaleString("cs-CZ")} Kč`, "Tržba"];
                  return [value, name];
                }}
                labelFormatter={(_lbl, payload) => {
                  const r = payload?.[0]?.payload;
                  if (!r?.date) return _lbl;
                  const d = new Date(`${r.date}T00:00:00`);
                  return d.toLocaleDateString("cs-CZ", { weekday: "long", day: "numeric", month: "long" });
                }}
              />
              <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                {chartData.map((row, i) => (
                  <Cell
                    key={i}
                    fill={row.revenue > 0 ? "#E5C880" : "#2a2521"}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
